import React, { lazy, Suspense, useEffect } from "react";
import { Box, CircularProgress, Container, Divider, Typography } from "@mui/material";
import useSEO from "../../hooks/useSEO";
import ChatIcon from '@mui/icons-material/Chat';
import AssignmentIcon from '@mui/icons-material/Assignment';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import HeaderLayout from "../../components/HeaderLayout/HeaderLayout";
import backdrop from "../../components/HeadingPage/assets/HeaderbgImage.svg";
import demoImage from "../../components/HeadingPage/assets/DemoImage.svg";
import financeBgImage from "../HomePage/assets/UseCasesBgImage.svg";
import invoiceProcessingImage from "../HomePage/assets/EmployeesImage.svg";
import fraudDetectionImage from "../HomePage/assets/EmployeesImage.svg";
import complianceImage from "../HomePage/assets/EmployeesImage.svg";
const FeaturesPageLayout = lazy(() => import("../../components/FeaturesPageLayout/FeaturesPageLayout"));
const UseCasesSection = lazy(() => import("../../components/UseCases/UseCase"));
const Testimonials = lazy(() => import("../../components/CompliantFold/Testimonials"));
const IntegrationSupport = lazy(() => import("../../components/IntegrationSupport/IntegrationSupport"));
const CustomUsecaseForm = lazy(() => import("../../components/CustomUsecaseForm/CustomUsecaseForm"));

// Enhanced loading component with better visual feedback
const LoadingFallback = () => (
    <Box sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        py: { xs: 6, sm: 8, md: 10 },
        minHeight: "200px"
    }}>
        <CircularProgress size={36} />
        <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, opacity: 0.8 }}
        >
            Loading...
        </Typography>
    </Box>
);

// Improved lazy loading component with better placeholder
const LazyComponent = ({ children, threshold = 0.1 }) => {
    const [isVisible, setIsVisible] = React.useState(false);
    const ref = React.useRef(null);

    React.useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold, rootMargin: "200px" }
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => {
            if (ref.current) {
                observer.unobserve(ref.current);
            }
        };
    }, [threshold]);

    return (
        <div ref={ref}>
            {isVisible ? children : (
                <Box sx={{
                    height: { xs: "150px", sm: "180px", md: "200px" },
                    width: "100%",
                    transition: "height 0.3s ease"
                }} />
            )}
        </div>
    );
};

export default function CustomerSupportPage() {
    useSEO({
        title: "Customer Support Solutions - Beanbag AI",
        description: "Enhance your customer support with Beanbag AI. Streamline ticket management, deploy intelligent chatbots, and deliver exceptional service with our intelligent automation.",
        canonical: "https://www.beanbag.ai/customer-support",
        keywords: "customer support automation, support chatbots, ticket management, AI customer service, support workflows",

        ogTitle: "Customer Support Automation | Beanbag AI",
        ogDescription: "Empower your support team with intelligent automation for ticket handling, chatbots, and personalized assistance",
        ogImage: "https://www.beanbag.ai/images/support-solution-card.jpg",
        ogUrl: "https://www.beanbag.ai/customer-support",
        ogType: "website",

        twitterCard: "summary_large_image",
        twitterSite: "@BeanbagAI",

        jsonLd: {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Customer Support Automation Solutions",
            "url": "https://www.beanbag.ai/customer-support",
            "description": "Beanbag AI empowers customer support teams to reduce manual effort and enhance customer experiences through intelligent automation.",
            "isPartOf": {
                "@type": "WebSite",
                "name": "Beanbag AI",
                "url": "https://www.beanbag.ai"
            }
        }
    });

    useEffect(() => {
        // Preload critical components
        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(() => {
                import("../../components/KeyFeatures/KeyFeatures");
                import("../../components/UseCases/UseCase");
            });
        }

        // Load non-critical components when tab is visible
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                import("../../components/CompliantFold/Compliant");
                import("../../components/IntegrationSupport/IntegrationSupport");
                import("../../components/CustomUsecaseForm/CustomUsecaseForm");
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    const supportFeatures = [
        {
            id: 1,
            title: "AI-Powered Chatbots and Virtual Assistants",
            icon: <ChatIcon fontSize="large" />,
            description: "Beanbag AI's easy to deploy chatbots use natural language processing (NLP) to understand and respond to customer inquiries in real time.",
            benefits: [
                { primary: "95% Faster Response", secondary: "Resolve routine queries instantly without human intervention." },
                { primary: "24/7 Availability", secondary: "Provide round-the-clock support across multiple channels." },
                { primary: "Consistent Accuracy", secondary: "Deliver accurate, up-to-date information every time." }
            ],
            howItWorks: [
                { primary: "Query Detection", secondary: "AI analyzes customer messages to identify intent." },
                { primary: "Response Generation", secondary: "Chatbots provide instant, contextually relevant answers." },
                { primary: "Escalation", secondary: "Route complex issues to human agents seamlessly." }
            ]
        },
        {
            id: 2,
            title: "Ticket Prioritization and Routing",
            icon: <AssignmentIcon fontSize="large" />,
            description: "Beanbag AI categorizes and prioritizes tickets based on urgency, topic, or complexity, ensuring efficient resource allocation.",
            benefits: [
                { primary: "Faster Resolution", secondary: "Reduce response times by routing tickets to the right agents." },
                { primary: "Improved Accuracy", secondary: "AI ensures tickets are assigned to the most suitable team." },
                { primary: "Workload Balance", secondary: "Distribute tickets evenly to prevent agent burnout." }
            ],
            howItWorks: [
                { primary: "Categorization", secondary: "AI analyzes ticket content to determine priority and category." },
                { primary: "Routing", secondary: "Tickets are automatically assigned to the best-suited agent or team." },
                { primary: "Learning", secondary: "The system improves routing decisions based on resolution outcomes." }
            ]
        },
        {
            id: 3,
            title: "Knowledge Base Management",
            icon: <MenuBookIcon fontSize="large" />,
            description: "Beanbag AI maintains and updates a centralized knowledge base, ensuring customers can find answers instantly.",
            benefits: [
                { primary: "80% Reduction in Query Volume", secondary: "Enable customers to resolve issues on their own." },
                { primary: "Automated Updates", secondary: "Keep information current with real-time data integration." },
                { primary: "Intelligent Recommendations", secondary: "Suggest relevant articles based on user behavior." }
            ],
            howItWorks: [
                { primary: "Content Curation", secondary: "AI scans and organizes knowledge base articles." },
                { primary: "Query Matching", secondary: "Match customer questions with relevant articles." },
                { primary: "Continuous Improvement", secondary: "Update content based on usage patterns and feedback." }
            ]
        },
        {
            id: 4,
            title: "Sentiment Analysis",
            icon: <SentimentSatisfiedAltIcon fontSize="large" />,
            description: "Beanbag AI analyzes customer interactions to detect emotions like frustration or satisfaction, enabling empathetic responses.",
            benefits: [
                { primary: "30% Higher Satisfaction", secondary: "Tailor responses to match customer emotional states." },
                { primary: "Proactive Escalation", secondary: "Flag frustrated customers for immediate human intervention." },
                { primary: "Trend Identification", secondary: "Spot recurring issues causing negative sentiment." }
            ],
            howItWorks: [
                { primary: "Emotion Detection", secondary: "AI analyzes tone and word choice in customer communications." },
                { primary: "Adaptive Responses", secondary: "Adjust tone and approach based on detected emotions." },
                { primary: "Feedback Loop", secondary: "Learn from successful emotional resolutions to improve future interactions." }
            ]
        }
    ];


    const supportCasesData = [
        {
            id: 1,
            image: invoiceProcessingImage,
            title: "End-to-End Support Automation",
            description:
                "From initial queries to follow-ups, Beanbag AI handles every step of the customer support journey",
        },
        {
            id: 2,
            image: fraudDetectionImage,
            title: "Seamless Integrations",
            description:
                "Connect with your existing CRM, ticketing systems, and communication tools for a unified support experience",
        },
        {
            id: 3,
            image: complianceImage,
            title: "Proven Results",
            description:
                "Join businesses that have reduced resolution times by 90%, improved satisfaction by 30%, and cut operational costs by 20%",
        },
    ];


    return (
        <Box sx={{
            position: "relative",
            width: "100%",
            overflow: "hidden"
        }}>
            {/* Hero Section */}
            <Box sx={{
                mb: { xs: 4, sm: 6, md: 8, lg: 10 },
                px: { xs: 0, sm: 2, md: 3 }
            }}>
                <HeaderLayout
                    backgroundImage={backdrop}
                    title="Automate Routine Tasks, Deliver Exceptional Support"
                    subtitle="Beanbag AI empowers customer support teams to reduce manual effort, and enhance customer experiences. Whether it's resolving common queries, managing tickets, or providing personalized assistance"
                    imageUrl={demoImage}
                    imageAlt="Platform Demo"
                    ctaButtonText="Get Started"
                    ctaButtonLink="https://flow.beanbag.ai/signup"
                    secondaryButtonText="Request a Demo"
                    showSecondaryButton={true}
                />
            </Box>

            {/* Features Section */}
            <Box sx={{
                px: { xs: 2, sm: 3, md: 4 },
                py: { xs: 2, sm: 3, md: 0 }
            }}>
                <LazyComponent>
                    <Suspense fallback={<LoadingFallback />}>
                        <FeaturesPageLayout
                            title={
                                <>
                                    Why Beanbag AI for Customer Support?
                                </>
                            }
                            description="Customer support teams often struggle with high volumes of repetitive tasks, inconsistent customer experiences, and limited visibility into customer needs. Beanbag AI transforms these challenges into opportunities for exceptional service."
                            sectionTitle="How It Works"
                            features={supportFeatures}
                            backgroundColor="#121212"
                            containerProps={{
                                py: { xs: 6, sm: 8, md: 10, lg: 12 },
                                px: { xs: 2, sm: 3, md: 4 }
                            }}
                        />
                    </Suspense>
                </LazyComponent>
            </Box>

            {/* Use Cases Section */}
            <Box sx={{
               px: { xs: 2, sm: 3, md: 4 },
               py: { xs: 4, sm: 6, md: 8 }
            }}>
                <LazyComponent>
                    <Suspense fallback={<LoadingFallback />}>
                        <UseCasesSection
                            title="Why Choose Beanbag AI"
                            backgroundImage={financeBgImage}
                            cardsData={supportCasesData}
                        />
                    </Suspense>
                </LazyComponent>
            </Box>

            {/* Testimonials Section with Improved Container */}
            <Container
                maxWidth={false}
                sx={{
                    width: "100%",
                    maxWidth: {
                        xs: "100%",
                        sm: "95%",
                        md: "90%",
                        lg: "1200px"
                    },
                    px: { xs: 2, sm: 3, md: 4 }, // Add responsive padding
                    py: { xs: 4, sm: 6, md: 8 },
                    mx: "auto", // Center the container
                }}>
                <Divider
                    sx={{
                        backgroundColor: "rgba(68, 68, 68, 0.8)",
                        width: "100%",
                        height: "1px",
                        my: { xs: 4, sm: 5, md: 6, lg: 7 },
                    }}
                />
                <Typography
                    variant="h4"
                    component="h2"
                    sx={{
                        fontSize: { xs: "24px", sm: "34px", md: "42px", lg: "48px" },
                        color: "white",
                        fontWeight: 600,
                        textAlign: "center",
                        textShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
                        mb: { xs: 3, sm: 4, md: 5, lg: 6 },
                        px: { xs: 1, sm: 2, md: 0 }
                    }}
                >
                    Trusted by Support Teams Worldwide
                </Typography>

                <Box sx={{
                    px: { xs: 0, sm: 2, md: 3 },
                    py: { xs: 2, sm: 3, md: 4 }
                }}>
                    <LazyComponent>
                        <Suspense fallback={<LoadingFallback />}>
                            <Testimonials />
                        </Suspense>
                    </LazyComponent>
                </Box>

                <Divider
                    sx={{
                        backgroundColor: "rgba(68, 68, 68, 0.8)",
                        width: "100%",
                        height: "1px",
                        mt: { xs: 4, sm: 5, md: 6 },
                        mb: { xs: 6, sm: 8, md: 10 }
                    }}
                />
            </Container>

            {/* Custom Usecase Form Section */}
            <Box sx={{
            }}>
                <LazyComponent>
                    <Suspense fallback={<LoadingFallback />}>
                        <CustomUsecaseForm />
                    </Suspense>
                </LazyComponent>
            </Box>
        </Box>
    );
}